import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FiGrid,
  FiUser,
  FiUsers,
  FiFileText,
  FiLogOut,
  FiMenu,
  FiChevronsLeft,
  FiChevronsRight,
  FiSun,
  FiMoon,
  FiBookOpen,
  FiZap,
  FiActivity,
} from 'react-icons/fi';
import { getStoredUser, logout } from '../../services/authService';
import { useTheme } from './ThemeProvider';
import { prefetchByPath } from '../../routes/lazyRoutes';
import Avatar from '../ui/Avatar';
import ImageLightbox from '../ui/ImageLightbox';
import './AppShell.css';

const NAV = [
  { to: '/dashboard',   label: 'Dashboard',   icon: FiGrid },
  { to: '/quizzes',     label: 'Quizzes',     icon: FiBookOpen },
  { to: '/ai/generate', label: 'AI generate', icon: FiZap },
  { to: '/history',     label: 'History',     icon: FiActivity },
  { to: '/profile',     label: 'Profile',     icon: FiUser },
];

const ADMIN_NAV = [
  { to: '/admin/users',      label: 'Users',      icon: FiUsers },
  { to: '/admin/ai-config',  label: 'AI config',  icon: FiZap },
  { to: '/admin/audit-logs', label: 'Audit logs', icon: FiFileText },
];

const COLLAPSE_KEY = 'aiq.sidebar.collapsed';

function NavItem({ to, label, icon: Icon, collapsed }) {
  const prefetch = prefetchByPath[to];
  const handlers = prefetch
    ? { onMouseEnter: prefetch, onFocus: prefetch, onTouchStart: prefetch }
    : {};
  return (
    <NavLink
      to={to}
      className="shell__link"
      title={collapsed ? label : undefined}
      {...handlers}
    >
      <span className="shell__link-icon">
        <Icon aria-hidden />
      </span>
      <span className="shell__link-label">{label}</span>
    </NavLink>
  );
}

const ALL_NAV = [...NAV, ...ADMIN_NAV];

const buildCrumbs = (pathname) => {
  const segs = pathname.split('/').filter(Boolean);
  if (!segs.length) return [];
  const out = [];
  let acc = '';
  segs.forEach((seg) => {
    acc += `/${seg}`;
    const match = ALL_NAV.find((n) => n.to === acc);
    out.push({
      to: acc,
      label: match ? match.label : seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' '),
    });
  });
  return out;
};

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const crumbs = useMemo(() => buildCrumbs(location.pathname), [location.pathname]);
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [avatarOpen, setAvatarOpen] = useState(false);
  const user = getStoredUser();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  // Close the mobile drawer whenever the route changes (user tapped a nav link).
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const onLogout = () => { logout(); navigate('/login'); };

  const shellClass = [
    'shell',
    open ? 'shell--open' : '',
    collapsed ? 'shell--collapsed' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={shellClass}>
      <div className="shell__ambient" aria-hidden>
        <div className="shell__ambient-aurora">
          <span className="shell__ambient-blob shell__ambient-blob--a" />
          <span className="shell__ambient-blob shell__ambient-blob--b" />
          <span className="shell__ambient-blob shell__ambient-blob--c" />
        </div>
        <div className="shell__ambient-grid" />
      </div>
      <aside className="shell__side">
        <div className="shell__brand">
          <span className="shell__logo" aria-hidden />
          <span className="shell__brand-name">AI Quiz</span>
          <button
            type="button"
            className="shell__collapse"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <FiChevronsRight aria-hidden /> : <FiChevronsLeft aria-hidden />}
          </button>
        </div>

        <nav className="shell__nav">
          <p className="shell__nav-label">Workspace</p>
          {NAV.map((n) => (
            <NavItem key={n.to} {...n} collapsed={collapsed} />
          ))}
          {isAdmin && (
            <>
              <p className="shell__nav-label">Admin</p>
              {ADMIN_NAV.map((n) => (
                <NavItem key={n.to} {...n} collapsed={collapsed} />
              ))}
            </>
          )}
        </nav>

        <div className="shell__me" onClick={() => navigate('/profile')}>
          <button
            type="button"
            className="shell__me-avatar"
            onClick={(e) => {
              e.stopPropagation();
              if (user?.avatar_url) setAvatarOpen(true);
              else navigate('/profile');
            }}
            aria-label={user?.avatar_url ? 'View profile picture' : 'Open profile'}
            title={user?.avatar_url ? 'View profile picture' : undefined}
          >
            <Avatar url={user?.avatar_url} name={user?.display_name || user?.username} size={36} />
          </button>
          <div className="shell__me-text">
            <span className="shell__me-name">{user?.display_name || user?.username}</span>
            <span className="shell__me-role">{user?.role}</span>
          </div>
          <button
            type="button"
            className="shell__logout"
            onClick={(e) => { e.stopPropagation(); onLogout(); }}
            title="Sign out"
            aria-label="Sign out"
          >
            <FiLogOut aria-hidden />
          </button>
        </div>
      </aside>

      <ImageLightbox
        open={avatarOpen}
        src={user?.avatar_url}
        alt={user?.display_name || user?.username || 'Avatar'}
        onClose={() => setAvatarOpen(false)}
      />

      <main className="shell__main">
        <header className="shell__top">
          <button
            type="button"
            className="shell__menu"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <FiMenu aria-hidden />
          </button>
          <button
            type="button"
            className="shell__collapse-desk"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <FiChevronsRight aria-hidden /> : <FiChevronsLeft aria-hidden />}
          </button>
          <nav className="shell__crumb" aria-label="Breadcrumb">
            <button
              type="button"
              className="shell__crumb-home"
              onClick={() => navigate('/dashboard')}
              aria-label="Home"
            >
              <FiGrid aria-hidden />
            </button>
            {crumbs.map((c, i) => (
              <React.Fragment key={c.to}>
                <span className="shell__crumb-sep" aria-hidden>/</span>
                {i === crumbs.length - 1 ? (
                  <span className="shell__crumb-current">{c.label}</span>
                ) : (
                  <button
                    type="button"
                    className="shell__crumb-link"
                    onClick={() => navigate(c.to)}
                  >
                    {c.label}
                  </button>
                )}
              </React.Fragment>
            ))}
          </nav>
          <div className="shell__top-spacer" />
          <button
            type="button"
            className="shell__theme"
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? <FiSun aria-hidden /> : <FiMoon aria-hidden />}
          </button>
        </header>
        <div className="shell__content">
          <Outlet />
        </div>
      </main>

      <div
        className="shell__scrim"
        onClick={() => setOpen(false)}
        aria-hidden
      />
    </div>
  );
}
